import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { CustomerOrder, CustomerOrderForm, CustomerOrderSearchForm } from 'entities/customer_order'

type CustomerOrdersResponse = PagingResponse & {
    results: CustomerOrder[]
}

export function useFetchCustomerOrdersApi(searchForm?: Form<CustomerOrderSearchForm>): IndexApiSet<CustomerOrdersResponse> & { execute: () => void } {
    const apiPath = 'customer_orders/'
    const api = useIndexApi<CustomerOrdersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchCustomerOrderApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `customer_orders/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostCustomerOrderApi(): ApiSet<BaseResponse> & { execute: (form: Form<CustomerOrderForm>) => void } {
    const apiSet = usePostApi<BaseResponse, CustomerOrderForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<CustomerOrderForm>) => {
        const apiPath = `customer_orders/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutCustomerOrderApi(): ApiSet<BaseResponse> & { execute: (form: Form<CustomerOrderForm>) => void } {
    const apiSet = usePutApi<BaseResponse, CustomerOrderForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CustomerOrderForm>) => {
        const apiPath = `customer_orders/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutCustomerOrdersApi(): ApiSet<BaseResponse> & { execute: (form: Form<CustomerOrderForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, CustomerOrderForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<CustomerOrderForm[]>) => {
        const apiPath = `customer_orders/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteCustomerOrderApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `customer_orders/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
